/**
 * CountriesView
 *
 * Countries breakdown table showing aggregated metrics per country.
 * Click on a country row to drill down (sets countryFilter).
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { CountryBreakdown } from '@/services/metricsAPI';

interface CountriesViewProps {
  countries: CountryBreakdown[];
  loading: boolean;
  onFilterByCountry: (countryCode: string) => void;
}

const formatSeconds = (s: number): string => {
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${(s % 60).toFixed(0)}s`;
};

export function CountriesView({
  countries,
  loading,
  onFilterByCountry,
}: CountriesViewProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="panel-glass-card-lg">
      <h2 className="section-heading-lg">{t('admin.metrics.countriesView')}</h2>

      {loading ? (
        <div className="text-white/50 text-center py-8">{t('common.loading')}</div>
      ) : countries.length === 0 ? (
        <div className="text-white/50 text-center py-8">{t('admin.metrics.noData')}</div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm text-white/80">
            <thead className="text-white/50 border-b border-white/10">
              <tr>
                <th className="text-left py-3 px-3">{t('admin.metrics.country')}</th>
                <th className="text-right py-3 px-3">{t('admin.metrics.operationCount')}</th>
                <th className="text-right py-3 px-3">{t('admin.metrics.uniqueUsers')}</th>
                <th className="text-right py-3 px-3">{t('admin.metrics.totalSpent')}</th>
                <th className="text-right py-3 px-3">{t('admin.metrics.duration')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {countries.map((country) => (
                <tr
                  key={country.country_code}
                  className="hover:bg-white/5 cursor-pointer"
                  onClick={() => onFilterByCountry(country.country_code)}
                >
                  <td className="py-3 px-3 font-medium">{country.country_code}</td>
                  <td className="py-3 px-3 text-right">{country.total_operations}</td>
                  <td className="py-3 px-3 text-right">{country.unique_users}</td>
                  <td className="py-3 px-3 text-right">{country.total_tokens.toLocaleString()}</td>
                  <td className="py-3 px-3 text-right">{formatSeconds(country.total_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
